/**
 * 📋 ADMIN BOOKING SERVICE
 * Service for admin-created bookings with cost calculation and elite tier discounts
 * Supports: Vehicle Rental, Airport Transfer, Yacht Rental
 */

import { buildApiUrl } from '@/utils/apiHelpers';
import { logger } from '@/utils/logger';

const log = logger.module('AdminBookingService');

// ==================== TYPES ====================

export interface CostBreakdownItem {
  label: string;
  amount: number;
  type: 'rental' | 'fee' | 'deposit' | 'addon';
  refundable?: boolean;
  discount?: number;
  originalAmount?: number;
}

export interface EliteTierDiscount {
  applied: boolean;
  tier?: 'Silver' | 'Gold' | 'Platinum';
  amount: number;
}

export interface CostCalculation {
  customerId: string;
  totalCost: number;
  costBreakdown: CostBreakdownItem[];
  discounts?: {
    eliteTierDiscount?: EliteTierDiscount;
  };
  _metadata?: {
    bookingFeeRate?: number;
    hostServiceFeePercentage?: number;
    vatPercentage?: number;
  };
}

export interface CalculateCostsRequest {
  customerId?: string; // Regular customer (required if not corporate)
  corporateAccountId?: string; // Corporate account (required if not regular customer)
  vehicleId?: string;
  yachtId?: string;
  startDate: string; // ISO8601
  endDate?: string;   // ISO8601 (optional for yacht-charter cost calculation)
  serviceCategory: 'vehicle-rental' | 'airport_transfer' | 'yacht-rental';

  // Vehicle-specific
  rentalOption?: string;

  // Airport transfer specific
  airportId?: string;
  zoneId?: string;
  tripType?: 'pickup' | 'dropoff' | 'round-trip'; // For airport transfers and yacht trips

  // Yacht trip specific
  destinationPricingId?: string;
}

export interface VehicleRentalBookingRequest {
  customerId?: string; // Regular customer (required if not corporate)
  corporateAccountId?: string; // Corporate account (required if not regular customer)
  vehicleId: string;
  startDate: string;
  endDate: string;
  pickupLocation: string;
  dropoffLocation?: string;

  rentalOption: 'self-drive' | 'chauffeur-driven';
  driverId?: string; // For chauffeur-driven bookings

  // Add-ons
  insurance?: boolean;
  insuranceCost?: number;
  delivery?: boolean;
  deliveryAddress?: string;
  deliveryCost?: number;

  // Costs
  totalAmount: number;
  securityDeposit?: number;

  // Payment
  paymentStatus: 'pending' | 'paid';
  paymentMethod?: string;

  // Admin overrides
  adminNotes?: string;
  skipAvailabilityCheck?: boolean;
}

export interface AirportTransferBookingRequest {
  customerId?: string; // Regular customer (required if not corporate)
  corporateAccountId?: string; // Corporate account (required if not regular customer)
  vehicleId: string;
  airportId: string;
  zoneId: string;
  tripType: 'pickup' | 'dropoff' | 'round-trip';
  startDate: string;
  pickupLocation: string;
  dropoffLocation?: string;
  driverId?: string;
  serviceCategory: 'airport_transfer'; // Required for database constraint

  // Add-ons
  insurance?: boolean;
  insuranceCost?: number;
  specialRequests?: string;

  // Costs
  totalAmount: number;
  securityDeposit?: number;

  // Payment
  paymentStatus: 'pending' | 'paid';
  paymentMethod?: string;

  // Admin overrides
  adminNotes?: string;
  skipAvailabilityCheck?: boolean;
}

export interface YachtRentalBookingRequest {
  customerId?: string; // Regular customer (required if not corporate)
  corporateAccountId?: string; // Corporate account (required if not regular customer)
  yachtId: string;
  startDate: string;
  endDate: string;
  pickupLocation: string;

  // Service type: cruise or trip
  serviceType: 'cruise' | 'trip';

  // For trips: destination-based
  destinationPricingId?: string;
  tripType?: 'pickup' | 'dropoff' | 'round-trip';
  pickupTime?: string; // For round trips

  // For cruises: time-based
  duration?: number; // hours

  // Add-ons
  catering?: boolean;
  cateringPreferences?: string;
  cateringCost?: number;

  // Costs
  baseCost: number; // Required by API contract
  totalAmount: number;
  securityDeposit?: number;

  // Payment
  paymentStatus: 'pending' | 'paid';
  paymentMethod?: string;

  // Admin overrides
  adminNotes?: string;
  skipAvailabilityCheck?: boolean;
}

export interface BookingResponse {
  success: boolean;
  message?: string;
  data?: {
    id: string;
    bookingNumber: string;
    customerId: string;
    customerName: string;
    customerEmail: string;
    vehicle?: any;
    yacht?: any;
    airport?: any;
    zone?: any;
    destination?: any;
    startDate: string;
    endDate?: string;
    pickupLocation: string;
    dropoffLocation?: string;
    serviceType: string;
    totalAmount: number;
    securityDeposit?: number;
    status: string;
    paymentStatus: string;
    createdAt: string;
    adminCreated: boolean;
  };
  error?: {
    code: string;
    message: string;
  } | string;
}

export interface CostCalculationResponse {
  success: boolean;
  data?: CostCalculation;
  error?: {
    code: string;
    message: string;
  } | string;
}

// ==================== SERVICE ====================

export const adminBookingService = {
  /**
   * 💰 Calculate booking costs with elite tier discounts
   */
  async calculateCosts(request: CalculateCostsRequest): Promise<CostCalculationResponse> {
    try {
      const url = buildApiUrl('/admin/bookings/calculate-costs', 'v2');

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });


      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Calculate Costs API Error ${response.status}:`, errorText);

        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { code: 'UNKNOWN_ERROR', message: errorText };
        }

        return {
          success: false,
          error: errorData.error || errorData,
        };
      }

      const result = await response.json();

      // Transform backend response to match CostCalculation interface
      // Backend returns: { totalDue, breakdown, rentalCost, bookingFee, vat, subtotal }
      // Frontend expects: { totalCost, costBreakdown }
      // All amounts are already in Naira (not kobo)
      const backendData = result.data || result;


      const transformedData: CostCalculation = {
        customerId: request.customerId,
        totalCost: backendData.totalDue || backendData.totalCost || 0,
        costBreakdown: backendData.breakdown || backendData.costBreakdown || [],
        discounts: backendData.discounts,
        _metadata: backendData._metadata,
      };


      // Log individual breakdown items for debugging
      if (transformedData.costBreakdown && transformedData.costBreakdown.length > 0) {
        transformedData.costBreakdown.forEach((item, index) => {
        });
      }

      return {
        success: true,
        data: transformedData,
      };
    } catch (error) {
      console.error('❌ Error calculating costs:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to calculate costs',
      };
    }
  },

  /**
   * 🚗 Create vehicle rental booking
   */
  async createVehicleRentalBooking(request: VehicleRentalBookingRequest): Promise<BookingResponse> {
    try {
      const url = buildApiUrl('/admin/bookings/vehicle-rental', 'v2');

      // Debug: Log what we're sending to backend
      console.log('🚗 Creating vehicle rental booking - corporateAccountId:', request.corporateAccountId);
      log.info('Creating vehicle rental booking', {
        customerId: request.customerId,
        corporateAccountId: request.corporateAccountId,
        isCorporate: !!request.corporateAccountId,
        vehicleId: request.vehicleId
      });

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Create Vehicle Rental API Error ${response.status}:`, errorText);

        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { code: 'UNKNOWN_ERROR', message: errorText };
        }

        return {
          success: false,
          error: errorData.error || errorData,
        };
      }

      const result = await response.json();

      // Debug: Log what backend returned
      console.log('✅ Booking created - backend returned corporateAccountId:', result.data?.corporateAccountId);
      log.info('Vehicle rental booking created', {
        bookingId: result.data?.id || result.data?.bookingId,
        hasCorporateAccountId: !!result.data?.corporateAccountId,
        corporateAccountId: result.data?.corporateAccountId
      });

      return {
        success: true,
        message: result.message || 'Vehicle rental booking created successfully',
        data: result.data,
      };
    } catch (error) {
      log.error('Error creating vehicle rental booking', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create vehicle rental booking',
      };
    }
  },

  /**
   * ✈️ Create airport transfer booking
   */
  async createAirportTransferBooking(request: AirportTransferBookingRequest): Promise<BookingResponse> {
    try {
      const url = buildApiUrl('/admin/bookings/airport-transfer', 'v2');

      // Debug: Log what we're sending to backend
      console.log('✈️ Creating airport transfer booking - corporateAccountId:', request.corporateAccountId);
      log.info('Creating airport transfer booking', {
        customerId: request.customerId,
        corporateAccountId: request.corporateAccountId,
        isCorporate: !!request.corporateAccountId,
        vehicleId: request.vehicleId
      });

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Create Airport Transfer API Error ${response.status}:`, errorText);

        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { code: 'UNKNOWN_ERROR', message: errorText };
        }

        return {
          success: false,
          error: errorData.error || errorData,
        };
      }

      const result = await response.json();

      // Debug: Log what backend returned
      console.log('✅ Airport transfer created - backend returned corporateAccountId:', result.data?.corporateAccountId);
      log.info('Airport transfer booking created', {
        bookingId: result.data?.id || result.data?.bookingId,
        hasCorporateAccountId: !!result.data?.corporateAccountId,
        corporateAccountId: result.data?.corporateAccountId
      });

      return {
        success: true,
        message: result.message || 'Airport transfer booking created successfully',
        data: result.data,
      };
    } catch (error) {
      log.error('Error creating airport transfer booking', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create airport transfer booking',
      };
    }
  },

  /**
   * 🛥️ Create yacht rental booking
   */
  async createYachtRentalBooking(request: YachtRentalBookingRequest): Promise<BookingResponse> {
    try {
      const url = buildApiUrl('/admin/bookings/yacht-rental', 'v2');

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Create Yacht Rental API Error ${response.status}:`, errorText);

        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { code: 'UNKNOWN_ERROR', message: errorText };
        }

        return {
          success: false,
          error: errorData.error || errorData,
        };
      }

      const result = await response.json();

      return {
        success: true,
        message: result.message || 'Yacht rental booking created successfully',
        data: result.data,
      };
    } catch (error) {
      console.error('❌ Error creating yacht rental booking:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create yacht rental booking',
      };
    }
  },
};

export default adminBookingService;
